/**
 * @module components/Dashboard/ComplianceChart
 * @description Gráfico de cumplimiento ISO por categoría de controles.
 *
 * Visualiza el porcentaje de cumplimiento de cada categoría ISO 27002
 * (Organizacional, Personas, Física, Tecnológica) con barras horizontales
 * con animación shimmer al cargar y color codificado por nivel:
 * - Verde: ≥ 80% cumplimiento
 * - Amarillo: 50–79%
 * - Rojo: < 50%
 *
 * También muestra el badge de acreditación cuando el cumplimiento global
 * supera el umbral configurado.
 *
 * @see MetricsCards para los KPIs numéricos complementarios
 * @see AccreditationBadge para el badge de acreditación condicional
 */
import React, { useMemo, useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Award, AlertCircle, ShieldAlert, ShieldCheck, Shield, Minus } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

const SHIMMER_STYLES = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const PRIORITY_LEVELS = [
  { key: 'critical', label: 'Crítica',  barColor: 'bg-gradient-to-r from-red-500 to-red-600',    icon: ShieldAlert, iconColor: 'text-red-600 dark:text-red-400',    badgeBg: 'bg-red-500' },
  { key: 'high',     label: 'Alta',     barColor: 'bg-gradient-to-r from-orange-500 to-orange-600', icon: ShieldAlert, iconColor: 'text-orange-500 dark:text-orange-400', badgeBg: 'bg-orange-500' },
  { key: 'medium',   label: 'Media',    barColor: 'bg-gradient-to-r from-yellow-400 to-yellow-500', icon: Shield,      iconColor: 'text-yellow-500 dark:text-yellow-400', badgeBg: 'bg-yellow-500' },
  { key: 'low',      label: 'Baja',     barColor: 'bg-gradient-to-r from-green-500 to-emerald-600', icon: ShieldCheck, iconColor: 'text-green-600 dark:text-green-400',  badgeBg: 'bg-green-500' },
] as const;

export function ComplianceChart() {
  const { filteredControls, filter, setFilter } = useData();
  const [activeTab, setActiveTab] = useState<'category' | 'priority'>('category');

  const chartData = useMemo(() => {
    const categoriesData = filteredControls.reduce((acc, control) => {
      if (!acc[control.category]) {
        acc[control.category] = { total: 0, completed: 0 };
      }
      acc[control.category].total += 1;
      if (control.status === 'completed') {
        acc[control.category].completed += 1;
      }
      return acc;
    }, {} as Record<string, { total: number; completed: number }>);

    return Object.entries(categoriesData)
      .map(([category, data]) => ({
        category,
        total: data.total,
        completed: data.completed,
        percentage: data.total > 0 ? (data.completed / data.total) * 100 : 0
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [filteredControls]);

  const priorityData = useMemo(() =>
    PRIORITY_LEVELS.map(level => {
      const group = filteredControls.filter(c => c.priority === level.key);
      const completed = group.filter(c => c.status === 'completed').length;
      return {
        ...level,
        total: group.length,
        completed,
        percentage: group.length > 0 ? (completed / group.length) * 100 : 0,
      };
    }).filter(d => d.total > 0),
  [filteredControls]);

  // Encontrar mejor y peor categoría
  const bestCategory = chartData.length > 0 ? chartData[0] : null;
  const worstCategory = chartData.length > 0 ? chartData[chartData.length - 1] : null;

  // Función para obtener el color de la barra según el porcentaje
  const getBarGradient = (percentage: number): string => {
    if (percentage >= 80) return 'bg-gradient-to-r from-green-500 to-emerald-600';
    if (percentage >= 60) return 'bg-gradient-to-r from-blue-500 to-cyan-600';
    if (percentage >= 40) return 'bg-gradient-to-r from-yellow-500 to-orange-500';
    return 'bg-gradient-to-r from-orange-500 to-red-500';
  };

  // Función para obtener badge de estado
  const getStatusBadge = (percentage: number): { icon: React.ComponentType<{ className?: string }>, color: string, label: string } => {
    if (percentage >= 80) return { icon: Award, color: 'bg-green-500', label: 'Excelente' };
    if (percentage >= 60) return { icon: TrendingUp, color: 'bg-blue-500', label: 'Bueno' };
    if (percentage >= 40) return { icon: TrendingDown, color: 'bg-yellow-500', label: 'Regular' };
    return { icon: AlertCircle, color: 'bg-red-500', label: 'Requiere atención' };
  };

  // Función para manejar el clic en una categoría
  const handleCategoryClick = (category: string) => {
    // Si la categoría ya está filtrada, la removemos; si no, la agregamos
    const currentCategories = filter.category;
    const isAlreadyFiltered = currentCategories.includes(category);

    if (isAlreadyFiltered) {
      // Remover el filtro de esta categoría
      setFilter({ ...filter, category: currentCategories.filter(c => c !== category) });
    } else {
      // Agregar esta categoría al filtro
      setFilter({ ...filter, category: [category] });
    }
  };

  const hasActiveFilter = filter.category.length > 0;

  const hasActiveCategoryFilter = filter.category.length > 0;
  const hasActivePriorityFilter = filter.priority.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">

      {/* Header */}
      <div className="flex items-center space-x-2 mb-1">
        <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cumplimiento</h3>
        {(hasActiveCategoryFilter || hasActivePriorityFilter) && (
          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full">
            Filtro activo
          </span>
        )}
      </div>

      {/* Pestañas */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('category')}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'category'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Por Categoría
        </button>
        <button
          onClick={() => setActiveTab('priority')}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'priority'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Por Prioridad
        </button>
      </div>

      {/* ── Tab: Categoría ── */}
      {activeTab === 'category' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {hasActiveCategoryFilter
                ? `Filtrando por: ${filter.category.join(', ')} • Clic para cambiar`
                : 'Progreso por área de control • Clic para filtrar'}
            </p>
            {bestCategory && worstCategory && chartData.length > 1 && (
              <div className="text-right text-xs space-y-1 shrink-0">
                <div className="flex items-center justify-end space-x-1">
                  <Award className="w-3 h-3 text-green-600" />
                  <span className="text-gray-600 dark:text-gray-400">Mejor: </span>
                  <span className="font-semibold text-green-600">{bestCategory.percentage.toFixed(0)}%</span>
                </div>
                <div className="flex items-center justify-end space-x-1">
                  <AlertCircle className="w-3 h-3 text-orange-600" />
                  <span className="text-gray-600 dark:text-gray-400">Atención: </span>
                  <span className="font-semibold text-orange-600">{worstCategory.percentage.toFixed(0)}%</span>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4 min-h-[340px] flex flex-col justify-center">
            {chartData.map((item) => {
              const isBest = bestCategory?.category === item.category && chartData.length > 1;
              const isWorst = worstCategory?.category === item.category && chartData.length > 1;
              const isFiltered = filter.category.includes(item.category);
              const statusBadge = getStatusBadge(item.percentage);
              const StatusIcon = statusBadge.icon;
              return (
                <div
                  key={item.category}
                  onClick={() => handleCategoryClick(item.category)}
                  className={`group hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-lg transition-all cursor-pointer ${
                    isBest ? 'ring-2 ring-green-500/30 bg-green-50/30 dark:bg-green-900/10' : ''
                  } ${
                    isWorst ? 'ring-2 ring-orange-500/30 bg-orange-50/30 dark:bg-orange-900/10' : ''
                  } ${
                    isFiltered ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2 flex-1">
                      <div className={`p-1 rounded-md ${statusBadge.color} group-hover:scale-110 transition-transform`}>
                        <StatusIcon className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.category}</span>
                      {isBest && (
                        <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                          <Award className="w-3 h-3" /><span>Top</span>
                        </span>
                      )}
                      {isWorst && item.percentage < 60 && (
                        <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center space-x-1 animate-pulse">
                          <AlertCircle className="w-3 h-3" /><span>Atención</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.completed}/{item.total}</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white min-w-[3rem] text-right">{item.percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
                    <div
                      className={`h-3 rounded-full transition-all duration-700 ${getBarGradient(item.percentage)} group-hover:shadow-lg relative overflow-hidden`}
                      style={{ width: `${item.percentage}%` }}
                    >
                      {item.percentage > 0 && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                             style={{ animation: 'shimmer 2s infinite' }} />
                      )}
                    </div>
                  </div>
                  <div className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.total - item.completed} controles pendientes • {statusBadge.label}
                      {isFiltered
                        ? <span className="text-blue-600 dark:text-blue-400 font-semibold"> • Clic para quitar filtro</span>
                        : <span className="text-gray-600 dark:text-gray-300 font-semibold"> • Clic para filtrar</span>
                      }
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {chartData.length === 0 && (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No hay datos disponibles</p>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Prioridad ── */}
      {activeTab === 'priority' && (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {hasActivePriorityFilter
              ? `Filtrando por: ${filter.priority.join(', ')} • Clic para cambiar`
              : 'Progreso por nivel de prioridad • Clic para filtrar'}
          </p>

          <div className="space-y-4 min-h-[340px] flex flex-col justify-center">
            {priorityData.map(item => {
              const isFiltered = filter.priority.includes(item.key);
              const Icon = item.icon;
              return (
                <div
                  key={item.key}
                  onClick={() => setFilter({ ...filter, priority: isFiltered ? [] : [item.key] })}
                  className={`group hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-lg transition-all cursor-pointer ${
                    isFiltered ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`p-1 rounded-md ${item.badgeBg} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</span>
                      {isFiltered && (
                        <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full">Filtrado</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.completed}/{item.total}</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white min-w-[3rem] text-right">{item.percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
                    <div
                      className={`h-3 rounded-full transition-all duration-700 ${item.barColor} relative overflow-hidden`}
                      style={{ width: `${item.percentage}%` }}
                    >
                      {item.percentage > 0 && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                             style={{ animation: 'shimmer 2s infinite' }} />
                      )}
                    </div>
                  </div>
                  <div className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.total - item.completed} controles pendientes
                      {isFiltered
                        ? <span className="text-blue-600 dark:text-blue-400 font-semibold"> • Clic para quitar filtro</span>
                        : <span className="text-gray-600 dark:text-gray-300 font-semibold"> • Clic para filtrar</span>
                      }
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {priorityData.length === 0 && (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No hay datos disponibles</p>
            </div>
          )}
        </>
      )}

      <style>{SHIMMER_STYLES}</style>
    </div>
  );
}